<template>
  <Combobox v-model="selected" :multiple="multiple">
    <div class="relative mt-1">
      <label
        :for="id"
        class="block mb-2 text-sm font-medium text-gray-900 dark:text-white text-right"
      >
        اختر الحلقات
      </label>

      <div
        class="relative w-full cursor-default overflow-hidden rounded-lg bg-white dark:bg-gray-700 text-left shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm"
      >
        <ComboboxInput
          ref="comboboxInput"
          class="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary focus:border-primary block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary dark:focus:border-primary disabled:bg-white dark:disabled:bg-gray-700 disabled:cursor-default focus:shadow-xl transition-all"
          :displayValue="getDisplayValue"
          @change="query = $event.target.value"
          @click="handleInputClick"
          @blur="handleBlur"
          :placeholder="isFocused ? 'اكتب للبحث...' : 'الكل'"
        />
        <ComboboxButton
          ref="comboboxButton"
          class="absolute inset-y-0 left-0 flex items-center pl-2"
        >
          <ChevronUpDownIcon class="h-5 w-5 text-gray-400 dark:text-gray-300" aria-hidden="true" />
        </ComboboxButton>
      </div>
      <TransitionRoot
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        @after-leave="query = ''"
      >
        <ComboboxOptions
          class="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black/5 dark:ring-gray-600 focus:outline-none sm:text-sm z-[999]"
        >
          <div
            v-if="filteredSessions.length === 0 && query !== ''"
            class="relative cursor-default select-none px-4 py-2 text-gray-700 dark:text-gray-300"
          >
            Nothing found.
          </div>

          <ComboboxOption
            v-if="!multiple"
            as="template"
            :value="allOptionValue"
            v-slot="{ selected, active }"
          >
            <li
              class="relative cursor-default select-none py-2 pl-10 pr-4 text-right"
              :class="{
                'bg-primary text-white': active,
                'text-gray-900 dark:text-gray-100': !active
              }"
            >
              <span
                class="block truncate"
                :class="{ 'font-medium': selected, 'font-normal': !selected }"
              >
                الكل
              </span>
              <span
                v-if="selected"
                class="absolute inset-y-0 left-0 flex items-center pl-3"
                :class="{ 'text-white': active, 'text-teal-600 dark:text-teal-400': !active }"
              >
                <CheckIcon class="h-5 w-5" aria-hidden="true" />
              </span>
            </li>
          </ComboboxOption>

          <ComboboxOption
            v-for="session in filteredSessions"
            :key="session.id"
            as="template"
            :value="session.id"
            v-slot="{ selected, active }"
          >
            <li
              class="relative cursor-default select-none py-2 pl-10 pr-4 text-right"
              :class="{
                'bg-primary text-white': active,
                'text-gray-900 dark:text-gray-100': !active
              }"
            >
              <span
                class="block truncate"
                :class="{ 'font-medium': selected, 'font-normal': !selected }"
              >
                {{ session.name }}
              </span>
              <span
                v-if="selected"
                class="absolute inset-y-0 left-0 flex items-center pl-3"
                :class="{ 'text-white': active, 'text-teal-600 dark:text-teal-400': !active }"
              >
                <CheckIcon class="h-5 w-5" aria-hidden="true" />
              </span>
            </li>
          </ComboboxOption>
        </ComboboxOptions>
      </TransitionRoot>
    </div>
  </Combobox>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot
} from '@headlessui/vue'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid'
import { uniqueId } from 'lodash'
import type { Session } from '@/types/global'

const props = withDefaults(
  defineProps<{
    sessions: Session[]
    multiple?: boolean
    allOptionValue?: number | undefined
  }>(),
  {
    multiple: true,
    allOptionValue: undefined
  }
)

const id = uniqueId()

const selected = defineModel<number[] | number | undefined>({
  default: undefined
})

// Only set initial value if it's not already set
if (selected.value === undefined) {
  if (props.multiple) {
    selected.value = props.sessions[0]?.id ? [props.sessions[0].id as number] : []
  } else {
    selected.value = props.allOptionValue // Default to "All" in single selection mode
  }
}

let query = ref('')
const isFocused = ref(false)
const comboboxButton = ref<any>(null)
const comboboxInput = ref<any>(null)
const isInitializing = ref(true)

// Allow initial value to be set
setTimeout(() => {
  isInitializing.value = false
}, 0)

function handleInputClick() {
  isFocused.value = true
  query.value = ''
  // Trigger the ComboboxButton to open the dropdown
  if (comboboxButton.value) {
    comboboxButton.value.$el.click()
  }
}

function handleBlur() {
  isFocused.value = false
}

// Watch for selection changes and blur the input
watch(selected, () => {
  // Don't blur on initial load
  if (isInitializing.value) return

  if (comboboxInput.value && comboboxInput.value.$el) {
    comboboxInput.value.$el.blur()
  }
  isFocused.value = false
  query.value = ''
})

function getDisplayValue(value: any) {
  // When focused and user is typing, show the query
  if (isFocused.value) {
    return query.value
  }

  // When not focused, show the selected value
  if (props.multiple) {
    const selectedSessions = props.sessions
      .filter((s) => (selected.value as number[])?.includes(s.id))
      .map((s) => s.name)
      .join(', ')
    return selectedSessions || ''
  } else {
    // For single selection mode - check both value parameter and selected.value
    const currentValue = value !== undefined ? value : selected.value

    if (currentValue === props.allOptionValue) {
      return 'الكل'
    }

    const session = props.sessions.find((s) => s.id === currentValue)
    return session?.name || 'الكل'
  }
}

let filteredSessions = computed(() =>
  query.value === ''
    ? props.sessions
    : props.sessions.filter((session) =>
        session.name
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.value.toLowerCase().replace(/\s+/g, ''))
      )
)
</script>
